import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import type { User, UserRole, InnovateFlowConfig } from '../types'
import { MfaChallengeType } from '../types/enums'
import {
  initiateAuth,
  respondToAuthChallenge,
  mfaMethodToChallengeResponseKey,
  parseMfasCanChoose,
  revokeToken,
  associateSoftwareTokenWithSession,
  verifySoftwareTokenWithSession,
  buildTotpOtpauthUri,
  type CognitoAuthResponse,
  type CognitoAuthenticationResult,
} from '../lib/cognito'
import { STORAGE_KEYS, AUTH_CONFIG } from '../constants/config'
import { performLogout, resetLogoutFlag, scheduleTokenRefresh } from '@secureembed/shared-crm'
import { queryClient } from '../lib/queryClient'
import { clearRecentSearches } from '../lib/recentSearches'
import { getMe, refreshTokens } from '../services/api'

declare global {
  interface Window {
    INNOVATEFLOW_CONFIG: InnovateFlowConfig
  }
}

/** Extra, backwards-compatible controls for the multi-step MFA login flow (D6). */
export interface LoginOptions {
  /**
   * Answer a surfaced SELECT_MFA_TYPE challenge by choosing this method token
   * (one of SMS_MFA / SOFTWARE_TOKEN_MFA / EMAIL_OTP). Only meaningful when a
   * prior login() call returned challengeName === 'SELECT_MFA_TYPE'.
   */
  selectMethod?: string
  /**
   * Phase-4 Cognito device-tracking opt-in ("Remember this device"). Accepted
   * now for a stable client contract; the device-confirmation flow is wired in
   * Phase 4, so it is currently informational only.
   */
  rememberDevice?: boolean
  /**
   * Answer a surfaced NEW_PASSWORD_REQUIRED challenge with this genuinely new
   * password. Only meaningful when a prior login() call returned
   * newPasswordRequired === true. Never the password used at the sign-in step.
   */
  newPassword?: string
}

/**
 * The login()/challenge contract the Login screen (and sibling frontend agents)
 * rely on. On terminal success every field is absent (`{}`). While a challenge
 * is pending, `mfaRequired` is true and the other fields describe it.
 */
export interface LoginChallengeResult {
  /** True when a code-entry OR method-selection challenge is pending (back-compat with the old SMS-only flow). */
  mfaRequired?: boolean
  /**
   * True when the account is in FORCE_CHANGE_PASSWORD and Cognito returned a
   * NEW_PASSWORD_REQUIRED challenge. The UI must collect a genuinely new
   * password and answer via login(email, tempPassword, undefined, { newPassword }).
   */
  newPasswordRequired?: boolean
  /**
   * The pending Cognito challenge name: a direct code challenge
   * (SOFTWARE_TOKEN_MFA / SMS_MFA / EMAIL_OTP) — answer via login(email, pw, code)
   * — or SELECT_MFA_TYPE — answer via login(email, pw, undefined, { selectMethod }).
   */
  challengeName?: string
  /**
   * The methods the user may choose from, parsed from
   * ChallengeParameters.MFAS_CAN_CHOOSE. Only populated when Cognito emits
   * SELECT_MFA_TYPE with more than one option (a single option is auto-answered).
   */
  availableMethods?: string[]
  /**
   * Masked delivery destination from ChallengeParameters.CODE_DELIVERY_DESTINATION
   * (e.g. "+61•••5489"). Present for SMS/email code challenges after the method
   * is challenged; absent for TOTP and pre-selection.
   */
  destination?: string
  /**
   * Story #4709 — present only when challengeName === 'MFA_SETUP': the base32
   * TOTP secret to enrol, for the manual-entry fallback.
   */
  secretCode?: string
  /**
   * Story #4709 — present only when challengeName === 'MFA_SETUP': the
   * otpauth:// URI to render as the enrolment QR code.
   */
  otpauthUri?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, mfaCode?: string, options?: LoginOptions) => Promise<LoginChallengeResult>
  /**
   * BUG-107: callers may supply their own navigation (e.g. Header's manual
   * sign-out SPA-navigates to /home). With no override, performLogout's
   * default hard redirect to /login applies (token expiry, 401 interceptor).
   * Exactly ONE navigation ever runs — never both.
   */
  logout: (opts?: { navigate?: () => void }) => void
  setActiveOrgId: (orgId: string) => void
}

// Exported so tests can wrap components in a real provider with a controlled value
// (no mocking of the hook required).
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEYS = {
  idToken: STORAGE_KEYS.ID_TOKEN,
  accessToken: STORAGE_KEYS.ACCESS_TOKEN,
  refreshToken: STORAGE_KEYS.REFRESH_TOKEN,
}

function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4) base64 += '='
  return atob(base64)
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.')
  if (parts.length !== 3) throw new Error('Invalid JWT')
  return JSON.parse(base64UrlDecode(parts[1]))
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token)
    const exp = payload.exp as number
    return Date.now() >= exp * 1000
  } catch {
    return true
  }
}

function extractUserFromToken(token: string): User {
  const payload = decodeJwtPayload(token)
  return {
    id: payload.sub as string,
    email: payload.email as string,
    name: payload.name as string | undefined,
    // Real OIDC claim Cognito sets on the ID token — true when the address was
    // verified (signup confirmation / admin-verified). Not inferred client-side.
    email_verified: payload.email_verified as boolean | undefined,
    role: (payload['custom:role'] as UserRole) || AUTH_CONFIG.DEFAULT_ROLE,
    // Kept separately from `role` so a screen can tell a recorded role from the
    // default one above it. Nothing else changes: `role` still defaults.
    role_claimed: typeof payload['custom:role'] === 'string' && !!payload['custom:role'],
    organization: payload['custom:organization'] as string | undefined,
    active_org_id: payload['custom:active_org_id'] as string | undefined,
    groups: Array.isArray(payload['cognito:groups']) ? (payload['cognito:groups'] as string[]) : [],
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  // The direct code challenge currently awaiting a code (SOFTWARE_TOKEN_MFA /
  // SMS_MFA / EMAIL_OTP). Determines which *_CODE ChallengeResponses key the
  // next login(email, pw, code) call uses. Null when no code challenge is
  // pending (e.g. before a method is selected for SELECT_MFA_TYPE).
  const [pendingChallenge, setPendingChallenge] = useState<string | null>(null)

  const logout = useCallback((opts?: { navigate?: () => void }) => {
    // Story #4684 Task #4697 (WS-12, Bug #2609/#2631): revoke the refresh
    // token server-side on logout, best-effort. Without this, a token an
    // attacker captured before logout (stolen access token, browser-history/
    // XSS leak) would keep working until it naturally expired — up to the 1h
    // TTL configured on the app client — even though the user believes they
    // signed out. Read the refresh token BEFORE performLogout clears storage
    // below. Fire-and-forget: logout must never hang or fail because Cognito
    // is briefly unreachable — the token still expires naturally within 1h
    // even if this call fails.
    const refreshToken = localStorage.getItem(TOKEN_KEYS.refreshToken)
    if (refreshToken) {
      revokeToken(window.INNOVATEFLOW_CONFIG, refreshToken).catch(() => {
        // Best-effort — see comment above.
      })
    }
    // Story #6760: recent searches belong to the person who typed them.
    clearRecentSearches()
    performLogout({
      storageKeys: [...Object.values(TOKEN_KEYS), 'activeAggregatorId'],
      onClear: () => setUser(null),
      // BUG-107: single navigation — the caller's SPA navigate when provided,
      // otherwise performLogout's default hard redirect to /login.
      navigate: opts?.navigate,
    })
  }, [])

  const setActiveOrgId = useCallback((orgId: string) => {
    setUser((prev) => prev ? { ...prev, active_org_id: orgId } : prev)
  }, [])

  // Clear all cached query data whenever the authenticated identity is LEFT —
  // logout, or switching to a different account in the same browser tab.
  // localStorage (and therefore the auth token) is shared across every tab of a
  // browser profile, so without this a previous user's cached lists (e.g.
  // ['projects']) could bleed into the next user's session on re-login. Keyed on
  // user id, so a mere active-org switch (same user) does NOT wipe the cache.
  //
  // Bug #5649: the clear must NOT fire on the signed-out -> signed-in
  // transition (null -> user). On every fresh page load `user` starts as null
  // and the token-restore effect below sets it a render later. React runs a
  // child's passive effects before its parent's, so by the time this effect
  // saw null -> user, every useQuery under the route (e.g. DocumentDetail's
  // seven) had already created its Query and started its fetch. clear()
  // destroys those Query objects with a silent cancel: the observers keep
  // pointing at orphaned, still-"pending" queries, the HTTP 200 that arrives
  // a few hundred ms later is dropped, isLoading never flips, and the page
  // shows a LoadingSkeleton forever. Nothing is cached before sign-in that
  // could bleed to the new user (a logout already cleared it, and a fresh
  // load starts empty), so skipping the null -> user case loses nothing.
  const prevUserIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const currentId = user?.id ?? null
    const prevId = prevUserIdRef.current
    prevUserIdRef.current = currentId
    if (prevId === undefined || prevId === null || prevId === currentId) return
    queryClient.clear()
  }, [user?.id])

  useEffect(() => {
    const idToken = localStorage.getItem(TOKEN_KEYS.idToken)
    if (idToken && !isTokenExpired(idToken)) {
      try {
        setUser(extractUserFromToken(idToken))
      } catch {
        logout()
      }
    }
    setLoading(false)
  }, [logout])

  // BUG-015: the JWT carries identity + role + groups, but NOT the caller's
  // organisation memberships — those live server-side and are returned by
  // `GET /users/me`. Without merging them into the context user, `user.org_memberships`
  // is always undefined, so InventorLayout's `isOrgAdmin` is false and the entire
  // Organisation nav group + its four pages are unreachable for real org owners/
  // admins. Fetch once per identity and merge; `groups` is backfilled from the
  // same source when the token happened to omit `cognito:groups`.
  const membershipsFetchedFor = useRef<string | null>(null)
  useEffect(() => {
    const uid = user?.id
    if (!uid) {
      // Reset on logout so a subsequent login (even same user) refetches.
      membershipsFetchedFor.current = null
      return
    }
    if (membershipsFetchedFor.current === uid) return
    membershipsFetchedFor.current = uid
    getMe()
      .then(({ user: me }) => {
        setUser((prev) => {
          if (!prev || prev.id !== uid) return prev
          return {
            ...prev,
            org_memberships: me.org_memberships ?? prev.org_memberships,
            groups: prev.groups && prev.groups.length ? prev.groups : (me.groups ?? prev.groups),
          }
        })
      })
      .catch(() => {
        // Non-fatal — the Organisation nav simply stays hidden. Allow a retry
        // on the next identity change by clearing the guard.
        membershipsFetchedFor.current = null
      })
  }, [user?.id])

  // BUG-014: proactively refresh the ID token shortly before it expires so an
  // idle session does not hard-die at the (hourly) expiry and force a logout.
  // The reactive api.ts 401 interceptor covers active requests; this covers the
  // idle case. Re-schedules itself after each successful refresh (setUser →
  // effect re-runs → reads the new token). Reference `user` so the schedule is
  // (re)armed on login/restore and torn down on logout.
  useEffect(() => {
    if (!user) return
    const idToken = localStorage.getItem(TOKEN_KEYS.idToken)
    if (!idToken) return
    const controller = scheduleTokenRefresh({
      idToken,
      refresh: async () => {
        const newIdToken = await refreshTokens()
        if (newIdToken) {
          setUser(extractUserFromToken(newIdToken))
        } else {
          logout()
        }
      },
    })
    return () => controller.cancel()
  }, [user, logout])

  // Bug #6918: pressing Back after signing out can restore the whole signed-in page from
  // the browser's back/forward cache - a frozen copy of the screen, so no React code runs
  // and no token check happens. A page restored that way with no token goes to /login.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && !localStorage.getItem(TOKEN_KEYS.idToken)) window.location.replace('/login')
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // Persist an AuthenticationResult exactly as the SMS-only flow used to —
  // SAME localStorage keys, SAME "store RefreshToken only when present" rule —
  // so token storage is byte-identical across every challenge type (D6).
  const persistAuthResult = (result: CognitoAuthenticationResult) => {
    localStorage.setItem(TOKEN_KEYS.idToken, result.IdToken)
    localStorage.setItem(TOKEN_KEYS.accessToken, result.AccessToken)
    if (result.RefreshToken) {
      localStorage.setItem(TOKEN_KEYS.refreshToken, result.RefreshToken)
    }
    setUser(extractUserFromToken(result.IdToken))
  }

  // Drive the challenge chain returned by InitiateAuth (or a SELECT_MFA_TYPE
  // answer) to a terminal state. Returns {} on success (tokens persisted, user
  // set) or a LoginChallengeResult describing what the UI must collect next.
  // Never sets the authenticated user unless Cognito returned an
  // AuthenticationResult — a pending challenge can NEVER be silently bypassed.
  const resolveChallenge = async (
    initial: CognitoAuthResponse,
    email: string,
  ): Promise<LoginChallengeResult> => {
    const config = window.INNOVATEFLOW_CONFIG
    let current = initial
    // Bounded loop: the only self-continuation is a single SELECT_MFA_TYPE
    // auto-answer, which then yields a code challenge — the cap guards against
    // any unexpected cycle rather than being a real depth requirement.
    for (let step = 0; step < 4; step++) {
      if (current.AuthenticationResult) {
        persistAuthResult(current.AuthenticationResult)
        setSessionToken(null)
        setPendingChallenge(null)
        return {}
      }

      const challenge = current.ChallengeName

      if (challenge === MfaChallengeType.NEW_PASSWORD_REQUIRED) {
        // The account is in FORCE_CHANGE_PASSWORD (an admin-created / temporary
        // credential). Cognito will not issue tokens until a NEW password is set.
        // NEVER auto-answer with the password just typed at the sign-in step —
        // that silently satisfies the forced rotation with the same secret, so
        // the user is never actually asked to change it (the exact defect this
        // branch previously had). Surface the challenge so the UI collects a
        // genuinely new password; login()'s NEW_PASSWORD_REQUIRED branch answers
        // it. Nothing is persisted here — no half-signed-in state if abandoned.
        if (!current.Session) throw new Error('Password change failed — no session returned.')
        setSessionToken(current.Session)
        setPendingChallenge(MfaChallengeType.NEW_PASSWORD_REQUIRED)
        return { newPasswordRequired: true, challengeName: MfaChallengeType.NEW_PASSWORD_REQUIRED }
      }

      if (challenge === MfaChallengeType.SELECT_MFA_TYPE) {
        const methods = parseMfasCanChoose(current.ChallengeParameters?.MFAS_CAN_CHOOSE)
        // Exactly one enrolled method → auto-answer with it (D6: the single
        // preferred method is challenged directly, no picker shown).
        if (methods.length === 1 && current.Session) {
          current = await respondToAuthChallenge(config, MfaChallengeType.SELECT_MFA_TYPE, current.Session, {
            USERNAME: email,
            ANSWER: methods[0],
          })
          continue
        }
        // Multiple methods → surface the choice; hold the session, no code
        // challenge is pending until the user selects a method.
        setSessionToken(current.Session ?? null)
        setPendingChallenge(null)
        return { mfaRequired: true, challengeName: MfaChallengeType.SELECT_MFA_TYPE, availableMethods: methods }
      }

      if (
        challenge === MfaChallengeType.SOFTWARE_TOKEN_MFA ||
        challenge === MfaChallengeType.SMS_MFA ||
        challenge === MfaChallengeType.EMAIL_OTP
      ) {
        setSessionToken(current.Session ?? null)
        setPendingChallenge(challenge)
        return {
          mfaRequired: true,
          challengeName: challenge,
          destination: current.ChallengeParameters?.CODE_DELIVERY_DESTINATION,
        }
      }

      // Story #4709: the pool is ON and this user has zero enrolled devices.
      // Associate a TOTP secret against the pending Session (never an
      // AccessToken — none exists yet) so the UI can show a QR/manual-key
      // enrolment step. Nothing is persisted here; if the user abandons this
      // screen (closes/refreshes/navigates away) there is no half-signed-in
      // state because persistAuthResult is never called until the final
      // RespondToAuthChallenge below succeeds.
      if (challenge === MfaChallengeType.MFA_SETUP) {
        if (!current.Session) throw new Error('Enrolment failed — no session returned.')
        const assoc = await associateSoftwareTokenWithSession(config, current.Session)
        if (!assoc.SecretCode || !assoc.Session) {
          throw new Error(assoc.message || 'Could not start authenticator enrolment.')
        }
        setSessionToken(assoc.Session)
        setPendingChallenge(MfaChallengeType.MFA_SETUP)
        return {
          mfaRequired: true,
          challengeName: MfaChallengeType.MFA_SETUP,
          secretCode: assoc.SecretCode,
          otpauthUri: buildTotpOtpauthUri(email, assoc.SecretCode),
        }
      }

      // No AuthenticationResult and no challenge we can answer.
      throw new Error(current.message || 'Authentication failed')
    }
    throw new Error('Authentication failed')
  }

  const login = async (
    email: string,
    password: string,
    mfaCode?: string,
    options?: LoginOptions,
  ): Promise<LoginChallengeResult> => {
    // BUG-107: manual sign-out can now SPA-navigate (no page reload), so the
    // module-level isLoggingOut flag survives into the next login. Clear it
    // here or the 401 interceptor would silently skip logouts after re-login.
    resetLogoutFlag()
    const config = window.INNOVATEFLOW_CONFIG

    // Branch A0 — answer a surfaced NEW_PASSWORD_REQUIRED challenge with a
    // genuinely new password (never the temporary one used at the sign-in step).
    if (options?.newPassword && sessionToken && pendingChallenge === MfaChallengeType.NEW_PASSWORD_REQUIRED) {
      const resp = await respondToAuthChallenge(config, MfaChallengeType.NEW_PASSWORD_REQUIRED, sessionToken, {
        USERNAME: email,
        NEW_PASSWORD: options.newPassword,
      })
      // Setting the new password can itself yield a follow-on challenge — e.g.
      // MFA_SETUP when the pool is ON, or a code challenge for an already-enrolled
      // user — so feed the response back through the same challenge machinery.
      // The NEW password becomes the working password for any subsequent step.
      return resolveChallenge(resp, email)
    }

    // Branch A — answer the pending direct code challenge with the entered code.
    if (mfaCode && sessionToken && pendingChallenge) {
      // Story #4709 — MFA_SETUP is a two-call completion, not a single
      // respondToAuthChallenge like the other code challenges: the code is
      // verified against the just-associated secret first (VerifySoftwareToken),
      // then the MFA_SETUP challenge itself must be answered with the Session
      // that call returns before Cognito will issue tokens.
      if (pendingChallenge === MfaChallengeType.MFA_SETUP) {
        const verify = await verifySoftwareTokenWithSession(config, sessionToken, mfaCode, 'InnovateFlow web')
        if (verify.Status !== 'SUCCESS' || !verify.Session) {
          throw new Error(verify.message || 'Enter the 6-digit code from your app.')
        }
        const complete = await respondToAuthChallenge(config, MfaChallengeType.MFA_SETUP, verify.Session, {
          USERNAME: email,
        })
        if (complete.AuthenticationResult) {
          persistAuthResult(complete.AuthenticationResult)
          setSessionToken(null)
          setPendingChallenge(null)
          return {}
        }
        throw new Error(complete.message || 'Could not complete sign-in after enrolment.')
      }

      // The response key is method-specific (SOFTWARE_TOKEN_MFA_CODE / SMS_MFA_CODE
      // / EMAIL_OTP_CODE), keyed off the challenge Cognito issued.
      const responseKey = mfaMethodToChallengeResponseKey(pendingChallenge)
      const data = await respondToAuthChallenge(config, pendingChallenge, sessionToken, {
        USERNAME: email,
        [responseKey]: mfaCode,
      })
      if (data.AuthenticationResult) {
        persistAuthResult(data.AuthenticationResult)
        setSessionToken(null)
        setPendingChallenge(null)
        return {}
      }
      // Wrong/expired code: surface Cognito's message (e.g. CodeMismatchException)
      // when present. The session + pending challenge are intentionally left
      // intact so the user can simply re-enter a fresh code (resubmit).
      throw new Error(data.message || 'MFA verification failed')
    }

    // Branch B — select a method for a surfaced SELECT_MFA_TYPE challenge.
    if (options?.selectMethod && sessionToken) {
      const data = await respondToAuthChallenge(config, MfaChallengeType.SELECT_MFA_TYPE, sessionToken, {
        USERNAME: email,
        ANSWER: options.selectMethod,
      })
      return resolveChallenge(data, email)
    }

    // Branch C — fresh InitiateAuth (password step).
    const data = await initiateAuth(config, email, password)
    return resolveChallenge(data, email)
  }

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, login, logout, setActiveOrgId }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
